import { useState, useRef } from 'react';
import logoIcon from '../../assets/icons/MeinGruenraum_Logo.png';

// --- Illustrations -----------------------------------------------------------

function IllustWelcome() {
  return (
    <div className="w-[200px] h-[140px] flex items-center justify-center">
      <div className="w-20 h-20 rounded-full bg-emerald-50 border-2 border-emerald-200 flex items-center justify-center">
        <img src={logoIcon} alt="" className="w-12 h-12 object-contain" aria-hidden="true" />
      </div>
    </div>
  );
}

function IllustPrinciple() {
  const rows = [
    { colorClass: 'bg-gray-200', label: 'Mrz', labelColor: 'text-gray-500', grow: 2 },
    { colorClass: 'bg-emerald-500', label: 'Apr', labelColor: 'text-emerald-600', grow: 3 },
    { colorClass: 'bg-gray-200', label: 'Mai', labelColor: 'text-gray-500', grow: 2 },
  ];
  return (
    <div className="w-[220px] flex flex-col gap-2 p-4 bg-gray-50 border border-gray-200 rounded-xl">
      {rows.map((row, i) => (
        <div key={i} className="flex items-center gap-2">
          <div
            className={`h-2.5 ${row.colorClass} rounded-full`}
            style={{ flex: row.grow }}
          />
          <span
            className={`text-[10px] font-mono ${row.labelColor}`}
            style={{ fontFamily: 'monospace' }}
          >
            {row.label}
          </span>
        </div>
      ))}
      <div
        className="mt-1 px-2 py-1.5 rounded-md bg-white border border-dashed border-gray-300 text-[11px] text-gray-600 text-center"
        style={{ fontFamily: 'monospace' }}
      >
        Ruhe → Pflege → Ruhe
      </div>
    </div>
  );
}

function IllustSetup() {
  const vertices = [
    { top: -6, left: 14 },
    { top: 2, right: -6 },
    { bottom: -6, right: 4 },
    { bottom: 2, left: -6 },
  ];
  return (
    <div className="w-[200px] h-[140px] relative">
      <div
        className="w-full h-full border-2 border-gray-900 rounded-lg"
        style={{
          backgroundImage:
            'repeating-linear-gradient(135deg, transparent, transparent 5px, rgba(16,185,129,0.08) 5px, rgba(16,185,129,0.08) 10px)',
          clipPath: 'polygon(10% 0%, 95% 5%, 100% 85%, 8% 100%)',
        }}
      />
      {vertices.map((pos, i) => (
        <div
          key={i}
          className="absolute w-3 h-3 rounded-full bg-white border-2 border-emerald-500"
          style={pos}
        />
      ))}
    </div>
  );
}

// --- Step config -------------------------------------------------------------

const STEPS = [
  {
    title: (
      <>
        Mein <span className="text-emerald-500">Grünraum.</span>
      </>
    ),
    subtitle: 'Ein Ort, an dem du deinen Garten planst und begleitest - ohne Druck.',
    Illustration: IllustWelcome,
    showSkip: true,
    cta: 'Weiter',
  },
  {
    title: (
      <>
        <span className="text-emerald-500">Grünraum</span> meldet sich, wenn etwas ansteht.
      </>
    ),
    subtitle: 'Den Rest der Zeit bleibt es still. Du musst nichts nachhalten.',
    Illustration: IllustPrinciple,
    showSkip: true,
    cta: 'Weiter',
  },
  {
    title: 'Lass uns deinen Garten einrichten.',
    subtitle: 'Vier kurze Schritte. Du kannst jederzeit abbrechen und später fortsetzen.',
    Illustration: IllustSetup,
    showSkip: false,
    cta: 'Einrichtung starten',
  },
];

// --- Main component ----------------------------------------------------------

const OnboardingFlow = ({ onDone }) => {
  const [step, setStep] = useState(0);
  const touchStartX = useRef(null);

  const advance = () => {
    if (step < STEPS.length - 1) setStep((s) => s + 1);
    else onDone();
  };

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (delta < -60 && step < STEPS.length - 1) setStep((s) => s + 1);
    else if (delta > 60 && step > 0) setStep((s) => s - 1);
  };

  const { title, subtitle, Illustration, showSkip, cta } = STEPS[step];

  return (
    <div
      className="fixed inset-0 bg-white flex flex-col px-6"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Top: skip */}
      <div className="flex justify-end mt-4" style={{ minHeight: 24 }}>
        {showSkip && (
          <button
            onClick={onDone}
            className="text-[13px] font-medium text-gray-500 bg-transparent border-none cursor-pointer p-0"
          >
            Überspringen
          </button>
        )}
      </div>

      {/* Middle: illustration + text */}
      <div className="flex-1 flex flex-col items-center justify-center gap-6 text-center w-full max-w-[460px] mx-auto">
        <Illustration />
        <div>
          <h1 className="text-xl font-bold text-gray-900 max-w-[320px] mx-auto leading-snug">
            {title}
          </h1>
          <p className="text-[13px] text-gray-500 mt-3 max-w-[320px] mx-auto leading-relaxed">
            {subtitle}
          </p>
        </div>
      </div>

      {/* Bottom: dots + CTA */}
      <div className="flex flex-col items-center gap-5 pb-10">
        {/* Dot indicators */}
        <div className="flex gap-2 items-center" role="tablist" aria-label="Onboarding-Fortschritt">
          {STEPS.map((_, i) => (
            <div
              key={i}
              role="tab"
              aria-selected={i === step}
              aria-label={`Schritt ${i + 1}`}
              className={`rounded-full transition-all duration-200 ${
                i === step ? 'w-5 h-2 bg-emerald-500' : 'w-2 h-2 bg-gray-300'
              }`}
            />
          ))}
        </div>

        <button
          onClick={advance}
          className="w-full max-w-[280px] bg-emerald-600 text-white text-[13px] font-medium py-3 rounded-lg hover:bg-emerald-700 transition-colors min-h-[44px]"
        >
          {cta}
        </button>
      </div>
    </div>
  );
};

export default OnboardingFlow;
